import { SurveyUserRoles } from '@constants/user';
import { SurveyDelegationProps } from '../types';
import { SURVEY } from '@constants/terminology';
import { useRemoveSurveyWorkgroupMutation } from '@api/workgroups';
import { generateErrorToast, generateToast } from '@g17eco/molecules/toasts';
import { DelegateWorkgroupModal } from './DelegateWorkgroupModal';
import { SurveyWorkgroup } from '@g17eco/types/workgroup';
import { WorkgroupsTableBase } from '@features/delegation';

const getQuestionCount = (
  { permission, utrvIds: workgroupUtrvIds }: SurveyWorkgroup,
  role: SurveyUserRoles.Stakeholder | SurveyUserRoles.Verifier,
  utrvIds: string[],
) => {
  if (!permission.roles.includes(role)) {
    return '-';
  }

  return utrvIds.length === workgroupUtrvIds.length ? 'All' : workgroupUtrvIds.length;
};

interface Props extends Pick<SurveyDelegationProps, 'survey' | 'metricGroups' | 'utrvIds'> {
  workgroups: SurveyWorkgroup[];
}

export const WorkgroupsTable = ({ workgroups, survey, metricGroups, utrvIds }: Props) => {
  const [deleteWorkgroup] = useRemoveSurveyWorkgroupMutation();

  const handleDelete = async (workgroupId: string) => {
    await deleteWorkgroup({ surveyId: survey._id, workgroupId })
      .unwrap()
      .then(() => {
        generateToast({
          title: 'Workgroup removed',
          color: 'success',
          message: 'Workgroup has been removed from the survey',
        });
      })
      .catch((e) => {
        generateErrorToast(e);
      });
  };

  return (
    <WorkgroupsTableBase
      entity={survey}
      workgroups={workgroups}
      metricGroups={metricGroups}
      entityLabel={SURVEY.SINGULAR}
      handlers={{ onDelete: handleDelete }}
      getCountDisplay={(workgroup, role) => getQuestionCount(workgroup, role, utrvIds)}
      renderDelegateModal={({ isOpen, toggle, editingWorkgroup }) => (
        <DelegateWorkgroupModal
          key={`delegate-workgroup-modal-${editingWorkgroup?._id}`}
          isOpen={isOpen}
          toggle={toggle}
          survey={survey}
          metricGroups={metricGroups}
          delegatedWorkgroups={workgroups}
          editingWorkgroup={editingWorkgroup}
        />
      )}
    />
  );
};
